import React, { useEffect, useState } from "react";
import { userInfo } from "../../types/Types";
import { useParams } from "react-router-dom";
import {
  Box,
  Text,
  VStack,
  Divider,
  HStack,
  Image,
  Card,
  CardBody,
  Grid,
  GridItem,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import brtAPI from "../../api/brtAPI";

// BRT Action Item Types

interface WelcomeCallBoard {
  ID: string | null;
  DriverBoardID: string | null;
  DriverName: string | null;
  DriverCode: string | null;
  Phone: string | null;
  Email: string | null;
  Completed: string | null;
  CompletedBy: string | null;
  Sentiment: string | null;
  Notes: string | null;
  OrientationLocation: string | null;
  OrientationDate: string | null;
  Recruiter: string | null;
  Division: string | null;
  CostCenter: string | null;
  DriverType: string | null;
  Note: string | null;
  Board: string | null;
  TeamDriver: string | null;
  TeamWith: string | null;
}

const WelcomeCall: React.FC<userInfo> = ({
  userName,
  userEmail,
  userDepartment,
  powerBiReportToken,
}) => {
  const { ID } = useParams<{ ID: string }>();
  const [comment, setComment] = useState<string>("");
  const [rating, setRating] = useState<number>(0);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<WelcomeCallBoard | null>(null);
  const toast = useToast();

  // Function to fetch the data from the API
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const payload = {
        id: ID,
        action: "Fetch welcome call",
      };
      const response = await brtAPI(payload);
      setData(response[0]);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);
    try {
      if (rating === 0 || !comment) {
        toast({
          title: "Incomplete information",
          description:
            "Please rate the conversation and provide some details about it.",
          status: "warning",
          duration: 9000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      const payload = {
        id: ID,
        userEmail: userEmail,
        action: "Submit welcome call",
        notes: comment,
        sentiment: rating,
      };
      const response = await brtAPI(payload);
      if (response) {
        setData(response[0]);
        setComment("");
        setRating(0);
        toast({
          title: "Action item submitted.",
          description: "Thank you for submitting the action item.",
          status: "success",
          duration: 9000,
          isClosable: true,
          position: "top",
        });
      } else {
        throw new Error("No response received");
      }
    } catch (error) {
      toast({
        title: "An error occurred.",
        description:
          "Error submitting the action item. Please try again or contact support.",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSubmitted(false);
    }
  };

  // useEffect for initial render
  useEffect(() => {
    document.title = "Welcome Call";
    fetchData();
  }, [ID]);

  return (
    <Box
      height="calc(100% - 90px)"
      m="2vh"
      justifyContent="center"
      alignItems="center"
      display="flex"
    >
      <VStack
        width="900px"
        borderRadius="3xl"
        background="whiteAlpha.900"
        shadow="lg"
        padding="20px"
      >
        <Box width="100%">
          <HStack height="40px">
            <Image src="/logo.png" alt="Logo" height="100%" />
            <Text fontSize="lg" fontWeight="semibold" color="#1D61AA" ml="10px">
              Welcome Call
            </Text>
            <Spinner
              thickness="2px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="md"
              alignSelf="center"
              visibility={isLoading ? "visible" : "hidden"}
            />
          </HStack>
          <Divider mt="10px" />
        </Box>

        <Card
          width="100%"
          mt="10px"
          variant="filled"
          background="blackAlpha.50"
          visibility={isLoading ? "hidden" : "visible"}
        >
          <CardBody>
            <Text fontSize="sm" color="blackAlpha.800" fontWeight="semibold">
              DRIVER INFORMATION
            </Text>

            <Grid templateColumns="repeat(4, 1fr)" gap={3} mt="20px">
              {/* Driver Name */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Driver Name
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.DriverName}
                </Text>
              </GridItem>
              {/* Driver Code */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Driver Code
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.DriverCode}
                </Text>
              </GridItem>
              {/* Hired Date */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Orientation Date
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.OrientationDate
                    ? new Date(data?.OrientationDate).toLocaleDateString()
                    : ""}
                </Text>
              </GridItem>
              {/* Truck Number */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Orientation Location
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.OrientationLocation
                    ? data?.OrientationLocation
                    : "N/A"}
                </Text>
              </GridItem>
              {/* Phone */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Phone
                </Text>
                {/* Format phone number to US format */}
                <Text fontSize="md" color="blackAlpha">
                  {data?.Phone
                    ? data?.Phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
                    : "N/A"}
                </Text>
              </GridItem>
              {/* Email */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Email
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.Email ? data?.Email : "N/A"}
                </Text>
              </GridItem>
              {/* DriverType */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Driver Type
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.DriverType ? data?.DriverType : "N/A"}
                </Text>
              </GridItem>
              {/* Division */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Division
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.Division ? data?.Division : "N/A"}
                </Text>
              </GridItem>
              {/* Cost Center */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Cost Center
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.CostCenter ? data?.CostCenter : "N/A"}
                </Text>
              </GridItem>
              {/* Board */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Board
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.Board ? data?.Board : "N/A"}
                </Text>
              </GridItem>
              {/* Team Driver */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Team Driver
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.TeamDriver ? data?.TeamDriver : "N/A"}
                </Text>
              </GridItem>
              {/* Team With */}
              <GridItem colSpan={1}>
                <Text fontSize="sm" color="#1D61AA" fontWeight="semibold">
                  Team With
                </Text>
                <Text fontSize="md" color="blackAlpha">
                  {data?.TeamWith ? data?.TeamWith : "N/A"}
                </Text>
              </GridItem>
            </Grid>
          </CardBody>
        </Card>

        <Card
          width="100%"
          mt="10px"
          visibility={isLoading ? "hidden" : "visible"}
          bg={data?.Completed ? "green.100" : "gray.100"}
        >
          <CardBody>
            <Text fontSize="sm" color="blackAlpha.800" fontWeight="semibold">
              STATUS
            </Text>
            <Text fontSize="md" color="blackAlpha">
              {data?.Completed
                ? `This action item has been completed on ${new Date(
                    data?.Completed
                  ).toLocaleDateString()}`
                : "This action item has not been completed"}
            </Text>
          </CardBody>
        </Card>
        <Card
          width="100%"
          mt="10px"
          visibility={isLoading ? "hidden" : "visible"}
        >
          <CardBody>
            <Text fontSize="sm" color="blackAlpha.800" fontWeight="semibold">
              ACTION ITEM
            </Text>
            <Grid templateColumns="repeat(1, 1fr)" gap={3} mt="20px">
              {/* Comments using textarea, height locked to not be adjustable, and a max length of 999 characters */}
              <GridItem>
                <FormControl id="comment">
                  <FormLabel fontSize="sm" color="#1D61AA">
                    Comments
                  </FormLabel>
                  <Textarea
                    placeholder="Add your note here..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    size="sm"
                    resize="none"
                    borderRadius="md"
                    maxLength={999}
                    isDisabled={isSubmitted || data?.Completed ? true : false}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}></GridItem>
              {/* Rating using sad, neutral, happy face, emoji at xl font size, when selected, set rating to a number, 1, 2, 3, inside a form control with the label rating, and the background turns light gray to indicate that an emoji has been selected, keep the background the same, based on whether the item has been selected or not */}
              <GridItem>
                <FormControl id="rating">
                  <FormLabel fontSize="sm" color="#1D61AA">
                    Rating
                  </FormLabel>
                  <HStack spacing="10px">
                    <Button
                      variant="ghost"
                      size="xl"
                      onClick={() => setRating(1)}
                      bg={rating === 1 ? "gray.200" : "white"}
                      fontSize="4xl"
                    >
                      😞
                    </Button>
                    <Button
                      variant="ghost"
                      size="xl"
                      onClick={() => setRating(2)}
                      bg={rating === 2 ? "gray.200" : "white"}
                      fontSize="4xl"
                    >
                      😐
                    </Button>
                    <Button
                      variant="ghost"
                      size="xl"
                      onClick={() => setRating(3)}
                      bg={rating === 3 ? "gray.200" : "white"}
                      fontSize="4xl"
                    >
                      😊
                    </Button>
                  </HStack>
                </FormControl>
              </GridItem>
            </Grid>
            {/* Submit button on the right side of the card */}
          </CardBody>
          <Button
            mt="10px"
            colorScheme="whatsapp"
            isLoading={isSubmitted}
            alignSelf={"flex-end"}
            width="100px"
            m="20px"
            onClick={handleSubmit}
            isDisabled={data?.Completed ? true : false || isSubmitted}
          >
            Submit
          </Button>
        </Card>
      </VStack>
    </Box>
  );
};

export default WelcomeCall;
